<template>
  <div v-if="affiliateCustomizations" :style="appStyleProps">
    <div id="embeddedModals">
      <LazySignModal v-if="showLegalModal" />
    </div>
    <BankPullFailureToastHost />
    <div
      id="embeddedView"
      class="embedded grid-lg-aside"
    >
      <aside class="embedded-nav d-none d-lg-block">
        <!-- Pinned to the viewport like the portal sidebar (NewPortalSideNav's .portal-nav-container),
             so the bottom block (pull status chip, support info) stays on screen on pages taller
             than the viewport instead of sitting at the bottom of the document. Scrolls on its own
             when the step list doesn't fit. -->
        <div class="d-flex flex-column tw:sticky tw:top-0 tw:h-screen tw:overflow-y-auto">
          <NuxtImg v-if="!!affiliateCustomizations && !iframeHeaderVisible && !!appNavLogo"
              :src="formatImagePath(appNavLogo)"
              alt=""
              class="affiliate-customizations-app-nav-logo"/>
          <nav class="px-8 py-6 flex-grow-1">
            <AppConfigNav/>
          </nav>
          <div class="d-none d-lg-block">
            <!-- Mirrors the portal sidebar (NewPortalSideNav): the chip sits just above the support
                 block, unpilled, so it takes the affiliate's aside colors instead of a fixed white
                 pill. tw:pl-7 (28px) left-aligns it with SupportInfo's 28px inset. tw:empty:p-0
                 drops the padding when the chip is hidden (only Vue's v-if comment is left). -->
            <div class="tw:pl-7 tw:pb-2 tw:empty:p-0">
              <BankPullStatusChip dark />
            </div>
            <SupportInfo v-if="contactInfoEnabled" />
            <PoweredByLendio v-if="poweredByLendioEnabled" />
          </div>
        </div>
      </aside>
      <div data-smooth-scroll="main" class='embedded-content-container tw:relative'>
        <!-- Top right on mobile, mirroring the host's floating close button (embedded-app's
             .floating-close: top 20px, left 30px below lg) and sitting in .embedded-content's 48px
             top padding, above the progress bar. The progress bar's tw:mt-3 clears the chip's
             bottom edge (the chip is taller than the X it lines up with). -->
        <div class="d-lg-none tw:absolute tw:top-3 tw:right-[30px]">
          <BankPullStatusChip />
        </div>
        <section class='embedded-content'>
          <AppConfigMobileNav class="tw:mt-3" />
          <slot />
        </section>
        <EmbeddedFooter />
      </div>
      <ChatBot v-if="chatEnabled"/>
    </div>
  </div>
  <div v-else></div>
</template>

<script>
import { LazySignModal } from '#components'
import { mapState, storeToRefs } from 'pinia'

import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useApplicationConfigStore } from '~/store/application-config'
import { useBankConnectorStore } from '~/store/bank-connector'
import { useBorrowerStore } from '~/store/borrower'
import { useProgressStore } from '~/store/progress'
import { useRootStore } from '~/store/root'
import { useUserStore } from '~/store/user'

import AppConfigMobileNav from "~/components/app-config/AppConfigMobileNav.vue";
import AppConfigNav from '~/components/app-config/AppConfigNav.vue'
import BankPullFailureToastHost from '~/components/bank-pull-status/BankPullFailureToastHost.vue'
import BankPullStatusChip from '~/components/bank-pull-status/BankPullStatusChip.vue'
import ChatBot from '~/components/ChatBot.vue'
import EmbeddedFooter from '~/components/embedded/EmbeddedFooter.vue'
import formatImagePath from '~/libs/image-format'
import PoweredByLendio from '~/components/PoweredByLendio.vue'
import SmoothScroll from '~/libs/smooth-scroll'
import SupportInfo from '~/components/SupportInfo.vue'
import { useStatementPullStatus } from '~/composables/useStatementPullStatus'

export default {
  components: {
    AppConfigMobileNav,
    AppConfigNav,
    BankPullFailureToastHost,
    BankPullStatusChip,
    ChatBot,
    EmbeddedFooter,
    LazySignModal,
    PoweredByLendio,
    SupportInfo,
  },
  async setup() {
    const rootStore = useRootStore()
    const affiliateCustomizationsStore = useAffiliateCustomizationStore()
    const appConfigStore = useApplicationConfigStore()
    const bankConnectorStore = useBankConnectorStore()
    const progressStore = useProgressStore()
    const userStore = useUserStore()
    const borrowerStore = useBorrowerStore()
    const { showLegalModal } = storeToRefs(borrowerStore)

    rootStore.setPageTitle(affiliateCustomizationsStore.headTitlePrefix + ' - Application')

    rootStore.setLendioFavicons()

    useStatementPullStatus()
    appConfigStore.updateBankPullStatusIndicatorEnabled()

    useHead({
      title: rootStore.pageTitle,
      link: rootStore.favicons,
    })

    return {
      showLegalModal,
      bankConnectorStore,
      progressStore,
      userStore,
      formatImagePath,
    }
  },
  data() {
    return {}
  },
  watch: {
    '$route.path' () {
      SmoothScroll.goTo(0)
      SmoothScroll.scrollElementsTop('[data-smooth-scroll="main"]')
    }
  },
  computed: {
    ...mapState(useRootStore, {
      authUser: (store) => store.authUser,
    }),
    ...mapState(useAffiliateCustomizationStore, [
      'affiliateCustomizations',
      'appNavLogo',
      'appStyleProps',
      'chatEnabled',
      'contactInfoEnabled',
      'iframeHeaderVisible',
      'poweredByLendioEnabled',
    ]),
    ...mapState(useProgressStore, ['currentApp']),

  },
  async mounted() {
    await this.progressStore.fetchProgress()
    await this.progressStore.updateApplication()
    await this.userStore.loadFreshLogin()
    await useBorrowerStore().addLastTouch()

    // Hydrates status on load/refresh, since Pinia state resets and the Pusher subscription
    // above only catches pushes received while mounted. Banks for the popover are loaded by
    // store/bank-pull-status-ui.js, and only for borrowers in the experiment.
    this.bankConnectorStore.getStatementPullStatus()
  }
}
</script>

<style lang='scss'>
@import 'assets/styles/affiliate-customizations/affiliate-customization-mixins';

$app-aside-background-color: var(--app-aside-background-color);

.embedded-nav {
  z-index: 100;
  background-color: $app-aside-background-color;
  border-right: 1px solid #DCDBDB;

  // The chip's dark variant assumes the portal's dark aside; here the aside color is
  // affiliate-configurable, so follow the affiliate's nav text color (as SupportInfo does).
  .bank-pull-status-chip__bank-icon {
    color: $app-nav-text-primary;
  }
}

.affiliate-customizations-app-nav-logo {
  @include affiliateAppNavLogoPortal();
}

#embeddedView,
#embeddedBase,
#embeddedPortal,
#embeddedModals {
  @include affiliateCustomizedApp();
}

.embedded-content-container {
  max-width: 800px;
}

.mp-transition-enter-active {
  left: 100%;
  opacity: 0;
  transition: left 0.25s ease-out, opacity 0.15s ease-out;
}

.mp-transition-enter-active {
  position: relative;
}

.mp-transition-enter-to {
  opacity: 1;
  left: 0%;
}

.mp-transition-back-enter-active {
  right: 100%;
  opacity: 0;
  transition: right 0.25s ease-out, opacity 0.15s ease-out;
}

.mp-transition-back-enter-active {
  position: relative;
  @include media-breakpoint-up(lg) {
    right: 10%;
  }
}

.mp-transition-back-enter-to {
  opacity: 1;
  right: 0%;
}

.mp-transition-leave, .mp-transition-back-leave {
  opacity: 1;
}

.mp-transition-leave-active, .mp-transition-back-leave-active {
  transition: opacity 0.15s linear;
}

.mp-transition-leave-to, .mp-transition-back-leave-to {
  opacity: 0;
}
</style>
